// Organization Health — v1.1 §3.3, build-instruction §3.3. G5.
//
// Backend: E4, `GET /api/organization/health-topology`
// (`backend/routers/ops.py:4139`, lane BE-ORGHEALTH). Founder-only —
// `require_founder`, same as three of the four surfaces it composes.
//
// This is presentation only. The endpoint already does the composing work
// (bu/health + schedulers/health + integrations/health + circuit-breaker/
// status folded into one node/edge graph); this surface renders exactly what
// it returns and adds nothing. In particular it does NOT invent an overall
// "health score" — the backend deliberately returns per-node status only
// (§3.5's "no speculative judgments" doctrine, same one FIX-ANALYTICS-SCHEMA
// applied to the sibling endpoint), so none is shown here either.
//
// Coverage is partial by the backend's own design: only 10 of 49 scheduled
// jobs have a code-evidenced dependency edge. The response's `coverage_note`
// says so, and this surface renders that note rather than silently showing a
// graph that looks complete.
import React, { useMemo } from "react";

import { useBrainResource } from "../../../components/brain/lib/hooks";
import HealthGraph from "./HealthGraph";
import EmptyState from "../../components/EmptyState";
import PageHead, { SectionTitle } from "../../components/PageHead";

const STATES = {
  loading: {
    title: "Loading organization health…",
    detail: "Reading business-unit, scheduler, integration and circuit-breaker status.",
  },
  disabled: {
    title: "Organization health topology is not available",
    detail: "The endpoint returned 404 — it is not mounted in this environment.",
  },
  denied: {
    title: "Founder access required",
    detail:
      "The server refused this request. Organization health composes founder-only data (bu/health, schedulers/health, circuit-breaker/status), so this is what a non-founder sees even by direct URL.",
  },
  empty: {
    title: "No nodes reported",
    detail: "The endpoint responded with an empty graph — nothing to show yet.",
  },
  error: {
    title: "Could not read organization health",
    detail: "The request failed. Nothing has been changed.",
  },
};

const TYPE_LABELS = {
  business_unit: "Business Units",
  integration: "Integrations",
  scheduler_job: "Scheduled Jobs",
  system: "System",
};

const TYPE_ORDER = ["business_unit", "integration", "scheduler_job", "system"];

const STATUS_DOT = {
  green: "ok",
  red: "crit",
  yellow: "warn",
  unknown: "",
};

function resolveState(resource) {
  if (!resource) return "loading";
  if (resource.status === "loading") return "loading";
  if (resource.status === "disabled") return "disabled";
  if (resource.status === "error") {
    const code = resource.error && resource.error.response && resource.error.response.status;
    if (code === 403) return "denied";
    if (code === 404) return "disabled";
    return "error";
  }
  const nodes = (resource.data && resource.data.nodes) || [];
  if (nodes.length === 0) return "empty";
  return "ok";
}

function StatusDot({ status }) {
  return <span className={`dot ${STATUS_DOT[status] || ""}`} title={status} />;
}

function NodeCard({ node }) {
  return (
    <div className="card oh-node" data-status={node.status}>
      <div className="oh-node-head">
        <StatusDot status={node.status} />
        <span className="oh-node-label">{node.label}</span>
      </div>
      <div className="oh-node-meta dim mono">{node.id}</div>
      {node.source ? (
        <div className="oh-node-source faint mono">{node.source}</div>
      ) : null}
      {node.note ? <div className="oh-node-note dim">{node.note}</div> : null}
    </div>
  );
}

export default function OrganizationHealth() {
  const topology = useBrainResource("/organization/health-topology");
  const state = resolveState(topology);
  const data = topology.data || {};
  const nodes = useMemo(() => data.nodes || [], [data.nodes]);
  const edges = useMemo(() => data.edges || [], [data.edges]);

  const nodesByType = useMemo(() => {
    const groups = {};
    for (const n of nodes) {
      const t = n.type || "unknown";
      (groups[t] = groups[t] || []).push(n);
    }
    return groups;
  }, [nodes]);

  const statusCounts = useMemo(() => {
    const counts = { green: 0, red: 0, yellow: 0, unknown: 0 };
    for (const n of nodes) {
      counts[n.status] = (counts[n.status] || 0) + 1;
    }
    return counts;
  }, [nodes]);

  const edgesByFrom = useMemo(() => {
    const groups = {};
    for (const e of edges) {
      (groups[e.from] = groups[e.from] || []).push(e);
    }
    return groups;
  }, [edges]);

  return (
    <>
      <PageHead
        title="Organization Health"
        subtitle="Dependency graph across business units, integrations, scheduled jobs and the circuit breaker."
      />

      {state !== "ok" ? (
        <EmptyState title={STATES[state].title} detail={STATES[state].detail} />
      ) : (
        <div className="oh">
          {/* The same payload read as a shape rather than a roster. Nodes,
              statuses, edges and each edge's `evidence` string all come from
              the topology endpoint - the graph invents nothing, and a node
              with no evidenced edge is drawn unconnected because that is the
              finding the endpoint's own doctrine insists on. The grouped list
              below is unchanged and remains what you scan and search. */}
          <HealthGraph nodes={nodes} edges={edges} coverageNote={data.coverage_note} />

          <section className="oh-summary card" aria-label="Status summary">
            <span className="chip oh-count">
              <span className="dot ok" /> {statusCounts.green} healthy
            </span>
            <span className="chip oh-count">
              <span className="dot warn" /> {statusCounts.yellow} attention
            </span>
            <span className="chip oh-count">
              <span className="dot crit" /> {statusCounts.red} unhealthy
            </span>
            <span className="chip oh-count">
              <span className="dot" /> {statusCounts.unknown} unknown
            </span>
            {data.generated_at ? (
              <span className="oh-generated faint mono">as of {data.generated_at}</span>
            ) : null}
          </section>

          {TYPE_ORDER.filter((t) => nodesByType[t]).map((type) => (
            <section key={type} className="oh-group">
              <SectionTitle>{TYPE_LABELS[type] || type}</SectionTitle>
              <div className="oh-node-grid">
                {nodesByType[type].map((node) => (
                  <NodeCard key={node.id} node={node} />
                ))}
              </div>
            </section>
          ))}

          <section className="oh-edges">
            <SectionTitle>Dependency edges ({edges.length})</SectionTitle>
            {edges.length === 0 ? (
              <p className="dim">No evidenced edges between the nodes above.</p>
            ) : (
              <ul className="oh-edge-list">
                {Object.keys(edgesByFrom).map((from) => (
                  <li key={from} className="card oh-edge-group">
                    <div className="oh-edge-from mono">{from}</div>
                    <ul className="oh-edge-sub">
                      {edgesByFrom[from].map((e, i) => (
                        <li key={`${e.from}-${e.to}-${i}`} className="oh-edge">
                          <span className="oh-edge-rel dim">{e.relationship}</span>
                          <span className="oh-edge-arrow faint"> → </span>
                          <span className="oh-edge-to mono">{e.to}</span>
                          {e.evidence ? (
                            <div className="oh-edge-evidence faint">{e.evidence}</div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.coverage_note ? (
            <p className="oh-note dim">{data.coverage_note}</p>
          ) : null}
        </div>
      )}
    </>
  );
}
